import React, { cloneElement, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react"
import { ReactComponent as MoreVertIcon } from "assets/icons/more_vert.svg"
import { useLocation } from "react-router-dom";
import { APP_CONFIG } from "constants/constants";

interface FloatingButtonGroupProps {
	openingButton?: React.ReactNode;
	children: React.ReactNode;
	id?: string;
}

const FloatingButtonGroup: React.FC<FloatingButtonGroupProps> = ({ openingButton, children }) => {
	const location = useLocation()
	const openingButtonRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false)
	const [menuPos, setMenuPos] = useState<{ top?: number; bottom?: number; left?: number; right?: number; minWidth: number }>({
		minWidth: 0,
	})
	const menuId = useId()

	const closeMenu = useCallback(() => setIsOpen(false), [])

	const updateMenuPos = useCallback(() => {
		if (!openingButtonRef.current) return
		const rect = openingButtonRef.current.getBoundingClientRect()
		const estimatedHeight = menuRef.current?.offsetHeight ?? 160
		const spaceBelow = window.innerHeight - rect.bottom
		const placeAbove = spaceBelow < estimatedHeight + 8 && rect.top > spaceBelow
		const placeLeft = rect.right > window.innerWidth / 2
		setMenuPos({
			top: placeAbove ? undefined : rect.bottom + 4,
			bottom: placeAbove ? window.innerHeight - rect.top + 4 : undefined,
			left: placeLeft ? undefined : rect.left,
			right: placeLeft ? window.innerWidth - rect.right : undefined,
			minWidth: rect.width,
		})
	}, [])

	const toggleMenu = useCallback((event?: React.MouseEvent) => {
		event?.stopPropagation()
		setIsOpen((open) => !open)
	}, [])

	const trigger = isValidElement(openingButton)
		? cloneElement(openingButton as React.ReactElement<{ onClick?: (event: React.MouseEvent) => void }>, {
			onClick: (event: React.MouseEvent) => {
				(openingButton.props as { onClick?: (event: React.MouseEvent) => void }).onClick?.(event)
				toggleMenu(event)
			},
		})
		: <MoreVertIcon className={`fill-black h-8 w-8 cursor-pointer`} onClick={toggleMenu} />

	useEffect(() => {
		closeMenu()
	}, [location.pathname, closeMenu])

	useLayoutEffect(() => {
		if (!isOpen) return
		updateMenuPos()
	}, [isOpen, updateMenuPos])

	useEffect(() => {
		if (!isOpen) return

		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target as Node
			if (openingButtonRef.current?.contains(target) || menuRef.current?.contains(target)) {
				return
			}
			closeMenu()
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				closeMenu()
			}
		}

		window.addEventListener('resize', updateMenuPos)
		window.addEventListener('scroll', updateMenuPos, true)
		document.addEventListener('keydown', handleKeyDown)
		const timeoutId = window.setTimeout(() => {
			document.addEventListener('pointerdown', handlePointerDown)
		}, 0)
		return () => {
			window.clearTimeout(timeoutId)
			window.removeEventListener('resize', updateMenuPos)
			window.removeEventListener('scroll', updateMenuPos, true)
			document.removeEventListener('pointerdown', handlePointerDown)
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [isOpen, closeMenu, updateMenuPos])

	return (
		<div className="relative h-fit max-md:w-full">
			<div
				className="max-md:w-full"
				ref={openingButtonRef}
				aria-haspopup="menu"
				aria-expanded={isOpen}
				aria-controls={isOpen ? menuId : undefined}
			>
				{trigger}
			</div>
			{createPortal(
				<AnimatePresence>
					{isOpen && (
						<motion.div
							ref={menuRef}
							id={menuId}
							role="menu"
							initial={{ opacity: 0, y: -4 }}
							animate={{
								opacity: 1,
								y: 0,
								transition: {
									duration: APP_CONFIG.SHORT_ENTER_ANIMATION_DURATION
								}
							}}
							exit={{
								opacity: 0,
								y: -4,
								transition: {
									duration: 0.1
								}
							}}
							onClick={closeMenu}
							style={{
								position: 'fixed',
								top: menuPos.top,
								bottom: menuPos.bottom,
								left: menuPos.left,
								right: menuPos.right,
								minWidth: menuPos.minWidth,
								zIndex: 80,
							}}
							className="w-max bg-white border border-gray-200 rounded-md shadow-md flex flex-col py-1 overflow-hidden whitespace-nowrap select-none"
						>
							{children}
						</motion.div>
					)}
				</AnimatePresence>,
				document.body
			)}
		</div>
	);
};

export default FloatingButtonGroup;
